import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useLocations } from './useLocations';
import { usePermissions } from './usePermissions';
import { toast } from 'sonner';

export type AssistanceReason =
  | 'void_needed'
  | 'discount_approval'
  | 'customer_issue'
  | 'technical_problem'
  | 'cash_pickup'
  | 'other';

export type AssistanceStatus = 'pending' | 'acknowledged' | 'resolved' | 'cancelled';

export interface AssistanceRequest {
  id: string;
  location_id: string;
  register_id: string | null;
  session_id: string | null;
  requested_by: string;
  responded_by: string | null;
  reason: AssistanceReason;
  notes: string | null;
  status: AssistanceStatus;
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  // Joined fields
  requester_name?: string;
  register_name?: string;
  responder_name?: string;
}

export const ASSISTANCE_REASONS: Record<AssistanceReason, { label: string; description: string }> = {
  void_needed: {
    label: 'Void Needed',
    description: 'Need to void an item or order',
  },
  discount_approval: {
    label: 'Discount Approval',
    description: 'Need manager approval for a discount',
  },
  customer_issue: {
    label: 'Customer Issue',
    description: 'Customer complaint or dispute',
  },
  technical_problem: {
    label: 'Technical Problem',
    description: 'System or hardware issue',
  },
  cash_pickup: {
    label: 'Cash Pickup',
    description: 'Cash drawer is full, need pickup',
  },
  other: {
    label: 'Other',
    description: 'Other assistance needed',
  },
};

export function useAssistanceRequests() {
  const { user } = useAuth();
  const { currentLocation } = useLocations();
  const { hasPermission, effectiveRole } = usePermissions();
  const queryClient = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Check if user is a manager (can respond to assistance requests)
  const isManager =
    hasPermission('team.view') ||
    ['superadmin', 'primary_admin', 'owner', 'admin', 'manager'].includes(effectiveRole || '');

  // Fetch pending assistance requests at current location (for managers)
  const {
    data: pendingRequests,
    isLoading: pendingLoading,
    error: pendingError,
  } = useQuery({
    queryKey: ['assistance-requests', 'pending', currentLocation?.id],
    queryFn: async () => {
      if (!currentLocation?.id) return [];

      const { data, error } = await supabase
        .from('assistance_requests')
        .select(
          `
          *,
          requester:profiles!requested_by(first_name, last_name),
          register:registers(name),
          responder:profiles!responded_by(first_name, last_name)
        `
        )
        .eq('location_id', currentLocation.id)
        .in('status', ['pending', 'acknowledged'])
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching pending requests:', error);
        return [];
      }

      // Transform the data
      return (data || []).map((req: any) => ({
        ...req,
        requester_name: req.requester
          ? `${req.requester.first_name} ${req.requester.last_name}`
          : 'Unknown',
        register_name: req.register?.name || null,
        responder_name: req.responder
          ? `${req.responder.first_name} ${req.responder.last_name}`
          : null,
      })) as AssistanceRequest[];
    },
    enabled: !!currentLocation?.id && isManager,
    staleTime: 1000 * 5, // 5 seconds
    refetchInterval: 1000 * 10, // Poll every 10 seconds as backup
  });

  // Fetch current user's active request (for cashiers)
  const {
    data: myActiveRequest,
    isLoading: myRequestLoading,
  } = useQuery({
    queryKey: ['assistance-requests', 'mine', user?.id, currentLocation?.id],
    queryFn: async () => {
      if (!user?.id || !currentLocation?.id) return null;

      const { data, error } = await supabase
        .from('assistance_requests')
        .select('*')
        .eq('requested_by', user.id)
        .eq('location_id', currentLocation.id)
        .in('status', ['pending', 'acknowledged'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching my request:', error);
        return null;
      }

      return data as AssistanceRequest | null;
    },
    enabled: !!user?.id && !!currentLocation?.id,
    staleTime: 1000 * 5,
  });

  // Create assistance request mutation
  const createRequest = useMutation({
    mutationFn: async ({
      reason,
      notes,
      registerId,
      sessionId,
    }: {
      reason: AssistanceReason;
      notes?: string;
      registerId?: string;
      sessionId?: string;
    }) => {
      if (!user?.id || !currentLocation?.id) {
        throw new Error('User or location not available');
      }

      const { data, error } = await supabase
        .from('assistance_requests')
        .insert({
          location_id: currentLocation.id,
          register_id: registerId || null,
          session_id: sessionId || null,
          requested_by: user.id,
          reason,
          notes: notes || null,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assistance-requests', 'mine', user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['assistance-requests', 'pending', currentLocation?.id],
      });
    },
  });

  // Acknowledge request mutation (manager)
  const acknowledgeRequest = useMutation({
    mutationFn: async (requestId: string) => {
      if (!user?.id) throw new Error('User not available');

      const { data, error } = await supabase
        .from('assistance_requests')
        .update({
          status: 'acknowledged',
          responded_by: user.id,
          acknowledged_at: new Date().toISOString(),
        })
        .eq('id', requestId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assistance-requests'],
      });
    },
  });

  // Resolve request mutation (manager)
  const resolveRequest = useMutation({
    mutationFn: async (requestId: string) => {
      if (!user?.id) throw new Error('User not available');

      const { data, error } = await supabase
        .from('assistance_requests')
        .update({
          status: 'resolved',
          responded_by: user.id,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', requestId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assistance-requests'],
      });
    },
  });

  // Cancel request mutation (cashier - own request only)
  const cancelRequest = useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error } = await supabase
        .from('assistance_requests')
        .update({
          status: 'cancelled',
        })
        .eq('id', requestId)
        .eq('requested_by', user?.id) // Ensure only own request
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assistance-requests', 'mine', user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['assistance-requests', 'pending', currentLocation?.id],
      });
    },
  });

  // Play notification sound
  const playNotificationSound = () => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('/sounds/assistance-alert.mp3');
        audioRef.current.volume = 0.7;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Ignore autoplay errors - browser may block
      });
    } catch {
      // Ignore audio errors
    }
  };

  // Real-time subscription for managers
  useEffect(() => {
    if (!currentLocation?.id || !isManager) return;

    const channel = supabase
      .channel(`assistance:${currentLocation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'assistance_requests',
          filter: `location_id=eq.${currentLocation.id}`,
        },
        (payload) => {
          // New assistance request - show toast and play sound
          const request = payload.new as AssistanceRequest;
          const reasonLabel = ASSISTANCE_REASONS[request.reason]?.label || request.reason;

          toast('Assistance Requested', {
            description: `A cashier needs help: ${reasonLabel}`,
          });

          playNotificationSound();

          // Invalidate queries to refresh the list
          queryClient.invalidateQueries({
            queryKey: ['assistance-requests', 'pending', currentLocation.id],
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'assistance_requests',
          filter: `location_id=eq.${currentLocation.id}`,
        },
        () => {
          // Request updated - refresh the list
          queryClient.invalidateQueries({
            queryKey: ['assistance-requests', 'pending', currentLocation.id],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentLocation?.id, isManager, queryClient, toast]);

  // Real-time subscription for cashiers (to know when their request is acknowledged/resolved)
  useEffect(() => {
    if (!user?.id || !currentLocation?.id || isManager) return;

    const channel = supabase
      .channel(`my-assistance:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'assistance_requests',
          filter: `requested_by=eq.${user.id}`,
        },
        (payload) => {
          const request = payload.new as AssistanceRequest;

          if (request.status === 'acknowledged') {
            toast.success('Help is on the way!', {
              description: 'A manager has acknowledged your request.',
            });
          } else if (request.status === 'resolved') {
            toast.success('Request Resolved', {
              description: 'Your assistance request has been resolved.',
            });
          }

          // Refresh my request
          queryClient.invalidateQueries({
            queryKey: ['assistance-requests', 'mine', user.id],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, currentLocation?.id, isManager, queryClient, toast]);

  // Helper to get time ago string
  const getTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;

    return date.toLocaleDateString();
  };

  return {
    // Data
    pendingRequests: pendingRequests || [],
    myActiveRequest,

    // Loading states
    isLoading: pendingLoading || myRequestLoading,
    pendingLoading,
    myRequestLoading,

    // Errors
    pendingError,

    // Role checks
    isManager,

    // Mutations
    createRequest,
    acknowledgeRequest,
    resolveRequest,
    cancelRequest,

    // Helpers
    getTimeAgo,
    ASSISTANCE_REASONS,
  };
}
